import React, { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  fetchProjectSettings,
  fetchProviderCatalog,
  updateProjectSettings,
  SettingsConflictError,
} from '../../lib/api/projects';
import {
  KNOWN_AGENT_TYPES,
  type AgentModelOverride,
  type FallbackChainEntry,
  type ProjectSettings,
  type ProjectSettingsView,
  type ProviderCatalog,
  type ProviderId,
} from '../../../../shared/types/projects';
import {
  PLAN_ORDER,
  PLANS,
  planToTier,
  type ProjectPlan,
} from '../../../../shared/types/plans';
import { validateModelsConfig } from '../../../../shared/lib/projectValidation';
import { validateAdvisorConfig, DEFAULT_ADVISOR_MODEL, ADVISOR_MODEL_OPTIONS } from '../../../../shared/lib/advisor';
import { Link } from 'react-router-dom';
import { StatusBadge } from '../ui';
import { useProjectPlan } from '../../lib/hooks/useProjectPlan';

/**
 * FR-3 — multi-model editor.
 *
 * - tier picker filters provider dropdowns
 * - default provider+model with provider-required-field reveals
 *   (bedrock_region for bedrock; vertex_project + vertex_location for vertex)
 * - per-agent overrides table with the same constraints
 * - drag-to-reorder fallback chain via @dnd-kit
 * - inline field errors via shared validateModelsConfig
 *
 * Save flow piggybacks on PUT /projects/:id/settings — we merge the new
 * tier+models into the rest of the existing settings, then forward to the
 * settings endpoint with the same optimistic-locking version.
 */
export const ModelsTab: React.FC<{ projectId: string }> = ({ projectId }) => {
  const settingsQ = useQuery({
    queryKey: ['project', projectId, 'settings'],
    queryFn: () => fetchProjectSettings(projectId),
  });
  const catalogQ = useQuery({
    queryKey: ['catalog'],
    queryFn: fetchProviderCatalog,
    staleTime: 5 * 60 * 1000,
  });

  if (settingsQ.isLoading || catalogQ.isLoading) {
    return <div className="text-sm text-gray-500">Loading…</div>;
  }
  if (settingsQ.isError) {
    return (
      <div role="alert" className="text-sm text-red-600">
        {(settingsQ.error as Error).message}
      </div>
    );
  }
  if (catalogQ.isError) {
    return (
      <div role="alert" className="text-sm text-red-600">
        Failed to load provider catalog: {(catalogQ.error as Error).message}
      </div>
    );
  }
  if (!settingsQ.data || !catalogQ.data) return null;

  return (
    <ModelsEditor
      projectId={projectId}
      initial={settingsQ.data}
      catalog={catalogQ.data}
      onSaved={() => settingsQ.refetch()}
    />
  );
};

interface EditorProps {
  projectId: string;
  initial: ProjectSettingsView;
  catalog: ProviderCatalog;
  onSaved: () => void;
}

const ModelsEditor: React.FC<EditorProps> = ({ projectId, initial, catalog, onSaved }) => {
  const [view, setView] = useState<ProjectSettingsView>(initial);
  // UX-07: the plan is read-only here — authoritative on the billing record,
  // surfaced via the same `useProjectPlan` hook every project-scoped editor
  // uses (shares the `['project', id, 'settings']` query key with `initial`,
  // so this doesn't add a fetch).
  const { plan } = useProjectPlan(projectId);
  const [defaultOverride, setDefaultOverride] = useState<AgentModelOverride>(
    initial.settings.models?.default ?? {},
  );
  const [agents, setAgents] = useState<Record<string, AgentModelOverride>>(
    (initial.settings.models?.agents as Record<string, AgentModelOverride>) ?? {},
  );
  const [fallbackEnabled, setFallbackEnabled] = useState<boolean>(
    initial.settings.models?.fallback?.enabled ?? false,
  );
  const [chain, setChain] = useState<FallbackChainEntry[]>(
    initial.settings.models?.fallback?.chain ?? [],
  );
  // FORGE-162: advisor tool.
  const [advisorEnabled, setAdvisorEnabled] = useState<boolean>(
    initial.settings.models?.advisor?.enabled ?? false,
  );
  const [advisorModel, setAdvisorModel] = useState<string>(
    initial.settings.models?.advisor?.model ?? DEFAULT_ADVISOR_MODEL,
  );
  const [advisorMaxUses, setAdvisorMaxUses] = useState<number>(
    initial.settings.models?.advisor?.max_uses ?? 3,
  );
  const [advisorAgents, setAdvisorAgents] = useState<string[]>(
    initial.settings.models?.advisor?.agents ?? [],
  );

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflictMsg, setConflictMsg] = useState<string | null>(null);

  const allowedProviders =
    (catalog.plan_allowances && catalog.plan_allowances[plan]) ??
    catalog.tier_allowances[planToTier(plan)] ??
    [];

  // Build a draft settings object to feed the validator. UX-07: the plan
  // is read-only (billing-authoritative) — this tab never writes
  // `settings.plan`. We still derive the legacy `tier` alias from the
  // effective plan so the tier-based validator (and older readers of the
  // stored row) stay correct.
  const draftSettings = useMemo<ProjectSettings>(
    () => {
      const { plan: _billingPlan, ...restSettings } = view.settings;
      return {
        ...restSettings,
        tier: planToTier(plan),
        models: {
          default: defaultOverride,
          agents,
          fallback: { enabled: fallbackEnabled, chain },
          advisor: { enabled: advisorEnabled, model: advisorModel, max_uses: advisorMaxUses, agents: advisorAgents },
        },
      };
    },
    [view.settings, plan, defaultOverride, agents, fallbackEnabled, chain, advisorEnabled, advisorModel, advisorMaxUses, advisorAgents],
  );

  const errors = useMemo(() => [
    ...validateModelsConfig(draftSettings),
    ...validateAdvisorConfig({ enabled: advisorEnabled, model: advisorModel, max_uses: advisorMaxUses, agents: advisorAgents }, plan),
  ], [draftSettings, advisorEnabled, advisorModel, advisorMaxUses, advisorAgents, plan]);

  // Build a lookup so the per-agent + chain rows can render field-scoped errors.
  const errorByLocation = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const e of errors) {
      const colon = e.indexOf(':');
      const where = colon > 0 ? e.slice(0, colon) : e;
      const list = map.get(where) ?? [];
      list.push(e);
      map.set(where, list);
    }
    return map;
  }, [errors]);

  const isDirty =
    JSON.stringify(defaultOverride) !== JSON.stringify(view.settings.models?.default ?? {}) ||
    JSON.stringify(agents) !== JSON.stringify(view.settings.models?.agents ?? {}) ||
    fallbackEnabled !== (view.settings.models?.fallback?.enabled ?? false) ||
    JSON.stringify(chain) !== JSON.stringify(view.settings.models?.fallback?.chain ?? []) ||
    JSON.stringify({ enabled: advisorEnabled, model: advisorModel, max_uses: advisorMaxUses, agents: advisorAgents }) !==
      JSON.stringify(view.settings.models?.advisor ?? { enabled: false, model: DEFAULT_ADVISOR_MODEL, max_uses: 3, agents: [] });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleSave = async () => {
    setSaveError(null);
    setConflictMsg(null);
    if (errors.length > 0) {
      setSaveError('Resolve validation errors before saving.');
      return;
    }
    setSaving(true);
    try {
      const newView = await updateProjectSettings(projectId, {
        settings: draftSettings,
        expectedVersion: view.version === 0 ? null : view.version,
      });
      setView(newView);
      onSaved();
    } catch (err) {
      if (err instanceof SettingsConflictError) {
        setConflictMsg(err.message);
        setView(err.current);
      } else {
        setSaveError((err as Error).message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = Number(active.id);
    const newIndex = Number(over.id);
    if (Number.isNaN(oldIndex) || Number.isNaN(newIndex)) return;
    setChain((items) => arrayMove(items, oldIndex, newIndex));
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between text-sm gap-3">
        <div className="flex items-center gap-2 flex-wrap" data-testid="models-plan-chip">
          <StatusBadge tone="info">{PLANS[plan].label} plan</StatusBadge>
          <span className="text-slate-600">{allowedProviders.join(', ')}</span>
          <Link to="/account/plan" className="text-primary-500 hover:text-primary-600 hover:underline">
            Manage plan →
          </Link>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving || errors.length > 0}
          data-testid="save-models"
          title={`Settings version ${view.version}`}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded disabled:opacity-40 hover:bg-blue-700"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {conflictMsg && (
        <div role="alert" className="border border-yellow-300 bg-yellow-50 rounded p-2 text-xs text-yellow-900">
          {conflictMsg}
        </div>
      )}
      {saveError && (
        <div role="alert" className="border border-red-200 bg-red-50 text-red-800 text-xs rounded p-2">
          {saveError}
        </div>
      )}

      {/* --- Default provider+model --- */}
      <section className="border rounded p-3 space-y-2">
        <header className="text-xs font-medium text-gray-700">Default model</header>
        <ProviderModelPicker
          where="models.default"
          catalog={catalog}
          allowedProviders={allowedProviders}
          value={defaultOverride}
          onChange={setDefaultOverride}
          errors={errorByLocation.get('models.default.provider') ?? []}
        />
      </section>

      {/* --- Per-agent overrides --- */}
      <section className="border rounded p-3 space-y-2">
        <header className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-700 inline-flex items-center">
            Per-agent overrides
            <DefaultWorkflowPopover />
          </span>
          <AgentAddRow
            existing={Object.keys(agents)}
            onAdd={(agentType) =>
              setAgents((prev) => ({ ...prev, [agentType]: {} }))
            }
          />
        </header>
        {Object.keys(agents).length === 0 ? (
          <p className="text-xs text-gray-500">
            No per-agent overrides. Each agent will use the Default model above.
          </p>
        ) : (
          <div className="space-y-2">
            {Object.entries(agents).map(([agentType, override]) => (
              <div key={agentType} className="border rounded p-2 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs">{agentType}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${agentType} override`}
                    onClick={() =>
                      setAgents((prev) => {
                        const next = { ...prev };
                        delete next[agentType];
                        return next;
                      })
                    }
                    className="text-xs text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                </div>
                <ProviderModelPicker
                  where={`models.agents.${agentType}`}
                  catalog={catalog}
                  allowedProviders={allowedProviders}
                  value={override}
                  onChange={(next) =>
                    setAgents((prev) => ({ ...prev, [agentType]: next }))
                  }
                  errors={errorByLocation.get(`models.agents.${agentType}.provider`) ?? []}
                  rowErrors={
                    errors.filter((e) =>
                      e.startsWith(`models.agents.${agentType}:`),
                    )
                  }
                />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* --- Fallback chain --- */}
      <section className="border rounded p-3 space-y-2">
        <header className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-700">Fallback chain</span>
          <label className="flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={fallbackEnabled}
              onChange={(e) => setFallbackEnabled(e.target.checked)}
            />
            enabled
          </label>
        </header>
        <p className="text-xs text-gray-500">
          When the primary model errors or rate-limits, the runtime retries
          against this ordered list. Drag rows to reorder; the first entry is
          tried first.
        </p>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={chain.map((_, i) => String(i))}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-1">
              {chain.map((entry, i) => (
                <FallbackRow
                  key={i}
                  index={i}
                  entry={entry}
                  catalog={catalog}
                  allowedProviders={allowedProviders}
                  errors={errors.filter((e) =>
                    e.startsWith(`models.fallback.chain[${i}]`),
                  )}
                  onChange={(next) =>
                    setChain((prev) => {
                      const copy = prev.slice();
                      copy[i] = next;
                      return copy;
                    })
                  }
                  onRemove={() =>
                    setChain((prev) => prev.filter((_, j) => j !== i))
                  }
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <button
          type="button"
          disabled={allowedProviders.length === 0}
          onClick={() =>
            setChain((prev) => [...prev, { provider: allowedProviders[0], model: '' }])
          }
          className="text-xs border rounded px-2 py-1 hover:bg-gray-50 disabled:opacity-40"
        >
          + Add entry
        </button>
        {allowedProviders.length <= 1 && (
          <p className="text-[10px] text-gray-500">
            The {PLANS[plan].label} plan permits only {allowedProviders.join(', ')}. Upgrade for more.
          </p>
        )}
      </section>

      {/* --- Advisor tool (FORGE-162) --- */}
      <section className="space-y-2 border-t pt-3" data-testid="advisor-section">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-700">Advisor tool</span>
          <label className="inline-flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              data-testid="advisor-enabled"
              disabled={plan !== 'enterprise'}
              checked={advisorEnabled}
              onChange={(e) => setAdvisorEnabled(e.target.checked)}
              className="rounded"
            />
            <span className="text-gray-600">{advisorEnabled ? 'on' : 'off'}</span>
          </label>
        </div>
        <p className="text-[10px] text-gray-500">
          A fast executor agent (Haiku/Sonnet) consults an Opus advisor mid-run for a quality lift.
          Anthropic provider only; the advisor sub-inference is billed at the advisor model's rate.
        </p>
        {plan !== 'enterprise' && (
          <p className="text-[10px] text-amber-700">Requires the Enterprise plan.</p>
        )}
        {advisorEnabled && plan === 'enterprise' && (
          <div className="space-y-2 pl-1">
            <label className="flex items-center gap-2 text-xs">
              <span className="w-24 text-gray-600">Advisor model</span>
              <select
                data-testid="advisor-model"
                value={advisorModel}
                onChange={(e) => setAdvisorModel(e.target.value)}
                className="border rounded px-2 py-0.5 text-xs bg-white"
              >
                {ADVISOR_MODEL_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs">
              <span className="w-24 text-gray-600">Max uses / run</span>
              <input
                type="number" min={1} max={20}
                data-testid="advisor-max-uses"
                value={advisorMaxUses}
                onChange={(e) => setAdvisorMaxUses(Number(e.target.value))}
                className="w-20 border rounded px-2 py-0.5 text-xs"
              />
            </label>
            <div className="text-xs">
              <span className="text-gray-600">Attach to agents</span>
              <div className="flex flex-wrap gap-2 mt-1" data-testid="advisor-agents">
                {['backend', 'web', 'mobile', 'qa', 'solo'].map((a) => (
                  <label key={a} className="inline-flex items-center gap-1 border rounded px-2 py-0.5">
                    <input
                      type="checkbox"
                      data-testid={`advisor-agent-${a}`}
                      checked={advisorAgents.includes(a)}
                      onChange={(e) =>
                        setAdvisorAgents((prev) =>
                          e.target.checked ? [...prev, a] : prev.filter((x) => x !== a),
                        )
                      }
                    />
                    <span className="font-mono">{a}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Provider + model picker — used for the default, each per-agent override,
// and each fallback-chain row. Reveals required fields per provider.
// ---------------------------------------------------------------------------

/** First plan in PLAN_ORDER (cheapest → priciest) whose allowance list includes `providerId`. */
function planRequiredFor(providerId: ProviderId, catalog: ProviderCatalog): ProjectPlan | null {
  for (const p of PLAN_ORDER) {
    const allowed = (catalog.plan_allowances && catalog.plan_allowances[p]) ?? catalog.tier_allowances[planToTier(p)] ?? [];
    if (allowed.includes(providerId)) return p;
  }
  return null;
}

const ProviderModelPicker: React.FC<{
  where: string;
  catalog: ProviderCatalog;
  allowedProviders: ProviderId[];
  value: AgentModelOverride;
  onChange: (next: AgentModelOverride) => void;
  errors: string[];
  rowErrors?: string[];
}> = ({ where, catalog, allowedProviders, value, onChange, errors, rowErrors = [] }) => {
  const provider = value.provider;
  const models = provider ? catalog.providers[provider]?.models ?? [] : [];
  // UX-07: a saved override can reference a provider the *current* plan no
  // longer allows (e.g. it was chosen on a higher plan that later downgraded).
  // Surface an upgrade hint rather than silently disabling the field.
  const requiredPlanForCurrent = provider && !allowedProviders.includes(provider)
    ? planRequiredFor(provider, catalog)
    : null;

  const setProvider = (p: ProviderId | '') => {
    if (!p) {
      onChange({});
      return;
    }
    // Switching provider blows away provider-specific fields.
    onChange({ provider: p, model: '' });
  };

  return (
    <div className="space-y-1 text-xs">
      <div className="grid grid-cols-2 gap-2">
        <label className="space-y-0.5">
          <span className="block text-[10px] text-gray-500">provider</span>
          <select
            data-testid={`${where}-provider`}
            value={provider ?? ''}
            onChange={(e) => setProvider(e.target.value as ProviderId | '')}
            className="block w-full border rounded px-1 py-0.5 bg-white"
          >
            <option value="">— unset —</option>
            {(['anthropic', 'bedrock', 'openai', 'vertex', 'meta', 'kimi'] as ProviderId[]).map((p) => {
              const locked = !allowedProviders.includes(p);
              const requiredPlan = locked ? planRequiredFor(p, catalog) : null;
              const requiredLabel = requiredPlan ? PLANS[requiredPlan].label : 'a higher';
              return (
                <option
                  key={p}
                  value={p}
                  disabled={locked}
                  title={locked ? `Requires the ${requiredLabel} plan` : undefined}
                >
                  {p}{locked ? ` — requires ${requiredLabel} plan` : ''}
                </option>
              );
            })}
          </select>
        </label>
        <label className="space-y-0.5">
          <span className="block text-[10px] text-gray-500">model</span>
          <select
            data-testid={`${where}-model`}
            value={value.model ?? ''}
            disabled={!provider}
            onChange={(e) => onChange({ ...value, model: e.target.value })}
            className="block w-full border rounded px-1 py-0.5 bg-white disabled:bg-gray-50"
          >
            <option value="">— select —</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.id}
                {m.context_window
                  ? ` · ${Math.round(m.context_window / 1000)}k`
                  : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      {requiredPlanForCurrent && (
        <p className="text-[10px] text-amber-700">
          {provider} requires the {PLANS[requiredPlanForCurrent].label} plan.{' '}
          <Link to="/account/plan" className="underline">Upgrade →</Link>
        </p>
      )}

      {provider === 'bedrock' && (
        <label className="space-y-0.5 block">
          <span className="block text-[10px] text-gray-500">bedrock_region (required)</span>
          <input
            type="text"
            value={value.bedrock_region ?? ''}
            placeholder="us-east-1"
            onChange={(e) => onChange({ ...value, bedrock_region: e.target.value })}
            className="block w-full border rounded px-1 py-0.5 font-mono"
          />
        </label>
      )}
      {provider === 'vertex' && (
        <div className="grid grid-cols-2 gap-2">
          <label className="space-y-0.5">
            <span className="block text-[10px] text-gray-500">vertex_project (required)</span>
            <input
              type="text"
              value={value.vertex_project ?? ''}
              onChange={(e) => onChange({ ...value, vertex_project: e.target.value })}
              className="block w-full border rounded px-1 py-0.5 font-mono"
            />
          </label>
          <label className="space-y-0.5">
            <span className="block text-[10px] text-gray-500">vertex_location (required)</span>
            <input
              type="text"
              value={value.vertex_location ?? ''}
              onChange={(e) => onChange({ ...value, vertex_location: e.target.value })}
              className="block w-full border rounded px-1 py-0.5 font-mono"
            />
          </label>
        </div>
      )}

      {(errors.length > 0 || rowErrors.length > 0) && (
        <div role="alert" className="text-[10px] text-red-600 space-y-0.5">
          {[...errors, ...rowErrors].map((e, i) => (
            <div key={i}>· {e}</div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// DefaultWorkflowPopover — info icon that shows the out-of-the-box
// per-agent model assignments when no overrides are configured.
// Sourced from agent/run.js MODELS_ANTHROPIC (kept in sync manually;
// update here whenever run.js reviewer or planner assignments change).
// ---------------------------------------------------------------------------

// FORGE-411 (2026-11-01): Haiku tier on Haiku 5.5. FORGE-387 (2026-10-04): Sonnet-tier on Sonnet 5.5. FORGE-349 (2026-09-25): reviewers on Opus 5.5. FORGE-3XX: Sonnet 5 / Opus 5 —
// Anthropic's current lineup (see run.js MODELS_ANTHROPIC).
const DEFAULT_WORKFLOW: { agent: string; model: string }[] = [
  { agent: 'epic-planner',       model: 'claude-sonnet-5-5'},
  { agent: 'planner-review',     model: 'claude-opus-5-5'  },
  { agent: 'solution-architect', model: 'claude-haiku-5-5' },
  { agent: 'backend',            model: 'claude-sonnet-5-5'},
  { agent: 'backend-review',     model: 'claude-opus-5-5'  },
  { agent: 'web',                model: 'claude-sonnet-5-5'},
  { agent: 'web-review',         model: 'claude-opus-5-5'  },
  { agent: 'mobile',             model: 'claude-sonnet-5-5'},
  { agent: 'mobile-review',      model: 'claude-opus-5-5'  },
  { agent: 'qa',                 model: 'claude-haiku-5-5' },
  { agent: 'qa-validate',        model: 'claude-haiku-5-5' },
  { agent: 'devops',             model: 'claude-sonnet-5-5'},
  { agent: 'devops-fix',         model: 'claude-sonnet-5-5'},
  { agent: 'devops-review',      model: 'claude-opus-5-5'  },
  { agent: 'secops',             model: 'claude-sonnet-5-5'},
  { agent: 'solo',               model: 'claude-sonnet-5-5'},
  { agent: 'bugfix',             model: 'claude-sonnet-5-5'},
  { agent: 'docs-agent',         model: 'claude-sonnet-5-5'},
  { agent: 'init',               model: 'claude-sonnet-5-5'},
  { agent: 'bootstrap-planner',  model: 'claude-sonnet-5-5'},
];

const DefaultWorkflowPopover: React.FC = () => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  return (
    <span className="relative inline-flex items-center">
      <button
        ref={btnRef}
        type="button"
        aria-label="Show default model workflow"
        onClick={() => setOpen((o) => !o)}
        className="text-gray-400 hover:text-gray-600 focus:outline-none ml-1 align-middle"
        style={{ lineHeight: 1 }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 16 16"
          width="13"
          height="13"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm0 1.5a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13zM7.25 5a.75.75 0 1 1 1.5 0 .75.75 0 0 1-1.5 0zm-.25 2.5h2v4.5h-2V7.5z" />
        </svg>
      </button>

      {open && (
        <>
          {/* backdrop — click outside to close */}
          <span
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-label="Default model workflow"
            className="absolute left-5 top-0 z-20 w-72 rounded border border-gray-200 bg-white shadow-lg text-xs"
            style={{ minWidth: '18rem' }}
          >
            <div className="px-3 py-2 border-b border-gray-100 font-medium text-gray-700">
              Default workflow (no overrides)
            </div>
            <div className="px-3 py-2 text-[10px] text-gray-500 border-b border-gray-100">
              Applies when <span className="font-mono">models.agents</span> has no entry for that agent.
              Reviewers always use Opus; impl&nbsp;agents use Sonnet; architect&nbsp;&amp;&nbsp;QA use Haiku.
            </div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 text-[10px] text-gray-400 uppercase tracking-wide">
                  <th className="px-3 py-1 text-left font-medium">Agent</th>
                  <th className="px-3 py-1 text-left font-medium">Model (Anthropic)</th>
                </tr>
              </thead>
              <tbody>
                {DEFAULT_WORKFLOW.map(({ agent, model }) => (
                  <tr key={agent} className="even:bg-gray-50 hover:bg-blue-50">
                    <td className="px-3 py-0.5 font-mono text-[10px] text-gray-700 whitespace-nowrap">
                      {agent}
                    </td>
                    <td className="px-3 py-0.5 font-mono text-[10px] text-blue-700 whitespace-nowrap">
                      {model}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-3 py-1.5 text-[10px] text-gray-400 border-t border-gray-100">
              Bedrock uses equivalent cross-region inference profile IDs.
            </div>
          </div>
        </>
      )}
    </span>
  );
};

const AgentAddRow: React.FC<{
  existing: string[];
  onAdd: (agentType: string) => void;
}> = ({ existing, onAdd }) => {
  const [selected, setSelected] = useState<string>('');
  const available = KNOWN_AGENT_TYPES.filter((a) => !existing.includes(a));
  return (
    <div className="flex items-center gap-1 text-xs">
      <select
        aria-label="Add agent override"
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        className="border rounded px-1 py-0.5 bg-white"
      >
        <option value="">— add agent —</option>
        {available.map((a) => (
          <option key={a} value={a}>{a}</option>
        ))}
      </select>
      <button
        type="button"
        disabled={!selected}
        onClick={() => {
          if (!selected) return;
          onAdd(selected);
          setSelected('');
        }}
        className="border rounded px-2 py-0.5 hover:bg-gray-50 disabled:opacity-40"
      >
        +
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// FallbackRow — sortable row using @dnd-kit/sortable.
// ---------------------------------------------------------------------------

const FallbackRow: React.FC<{
  index: number;
  entry: FallbackChainEntry;
  catalog: ProviderCatalog;
  allowedProviders: ProviderId[];
  errors: string[];
  onChange: (next: FallbackChainEntry) => void;
  onRemove: () => void;
}> = ({ index, entry, catalog, allowedProviders, errors, onChange, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(index),
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`fallback-row-${index}`}
      className="border rounded p-2 bg-white flex items-start gap-2"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Reorder entry ${index + 1}`}
        className="text-gray-400 hover:text-gray-700 cursor-grab px-1 select-none"
      >
        ⋮⋮
      </button>
      <div className="flex-1">
        <ProviderModelPicker
          where={`models.fallback.chain[${index}]`}
          catalog={catalog}
          allowedProviders={allowedProviders}
          value={entry}
          onChange={onChange}
          errors={errors.filter((e) => e.endsWith('is not recognized') || e.includes('provider'))}
          rowErrors={errors.filter((e) => !e.includes('provider'))}
        />
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove fallback entry ${index + 1}`}
        className="text-xs text-red-600 hover:underline"
      >
        Remove
      </button>
    </div>
  );
};

export default ModelsTab;
